import { useState, useEffect } from 'react';
import axios from 'axios';
import { API, getAuthHeader } from '../App';
import { Card, CardContent } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { FileText, CheckCircle, XCircle, Clock, DollarSign, AlertTriangle, Edit, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency as formatDirham } from '../utils/currencyUtils';

const InsuranceClaims = () => {
  const [claims, setClaims] = useState([]);
  const [filteredClaims, setFilteredClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    paidByLessee: 0,
    paidByRider: 0,
    paidByCompany: 0
  });

  const [editForm, setEditForm] = useState({
    status: '',
    approved_amount: '',
    settlement_date: '',
    payment_method: '',
    notes: ''
  });

  useEffect(() => {
    fetchClaims();
  }, []);

  const fetchClaims = async () => {
    try {
      const response = await axios.get(`${API}/insurance-claims/enriched`, { headers: getAuthHeader() });
      const data = response.data;
      
      setClaims(data);
      setFilteredClaims(data);
      
      // Calculate stats
      const pending = data.filter(c => c.status === 'pending').length;
      const approved = data.filter(c => c.status === 'approved').length;
      const rejected = data.filter(c => c.status === 'rejected').length;
      const paidByLessee = data.filter(c => c.status === 'paid_by_lessee').length;
      const paidByRider = data.filter(c => c.status === 'paid_by_rider').length;
      const paidByCompany = data.filter(c => c.status === 'paid_by_company').length;
      
      setStats({
        total: data.length,
        pending,
        approved,
        rejected,
        paidByLessee,
        paidByRider,
        paidByCompany
      });
    } catch (error) {
      console.error('Error fetching claims:', error);
      toast.error('Failed to fetch insurance claims');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterClick = (filter) => {
    setActiveFilter(filter);
    
    switch(filter) {
      case 'pending':
        setFilteredClaims(claims.filter(c => c.status === 'pending'));
        break;
      case 'approved':
        setFilteredClaims(claims.filter(c => c.status === 'approved'));
        break;
      case 'rejected':
        setFilteredClaims(claims.filter(c => c.status === 'rejected'));
        break;
      case 'paid':
        setFilteredClaims(claims.filter(c => c.status === 'paid_by_lessee' || c.status === 'paid_by_rider' || c.status === 'paid_by_company'));
        break;
      default:
        setFilteredClaims(claims);
    }
  };

  const handleEditClick = (claim) => {
    setSelectedClaim(claim);
    setEditForm({
      status: claim.status,
      approved_amount: claim.approved_amount || claim.claim_amount,
      settlement_date: claim.settlement_date || '',
      payment_method: claim.payment_method || '',
      notes: claim.notes || ''
    });
    setEditDialogOpen(true);
  };

  const handleViewDetails = (claim) => {
    setSelectedClaim(claim);
    setDetailDialogOpen(true);
  };

  const handleUpdateClaim = async () => {
    try {
      // Prepare update data
      const updateData = {
        status: editForm.status,
        approved_amount: parseFloat(editForm.approved_amount),
        notes: editForm.notes
      };

      // Add optional fields if filled
      if (editForm.settlement_date) {
        updateData.settlement_date = editForm.settlement_date;
      }
      if (editForm.payment_method) {
        updateData.payment_method = editForm.payment_method;
      }

      await axios.put(
        `${API}/insurance-claims/${selectedClaim.id}`,
        updateData,
        { headers: getAuthHeader() }
      );
      
      toast.success('Claim updated successfully');
      setEditDialogOpen(false);
      fetchClaims();
    } catch (error) {
      console.error('Error updating claim:', error);
      toast.error('Failed to update claim');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Pending', className: 'bg-yellow-500', icon: Clock },
      approved: { label: 'Approved', className: 'bg-green-500', icon: CheckCircle },
      rejected: { label: 'Rejected', className: 'bg-red-500', icon: XCircle },
      paid_by_lessee: { label: 'Paid by Lessee', className: 'bg-blue-500', icon: DollarSign },
      paid_by_rider: { label: 'Paid by Rider', className: 'bg-purple-500', icon: DollarSign },
      paid_by_company: { label: 'Paid by Company', className: 'bg-indigo-500', icon: DollarSign }
    };

    const config = statusConfig[status] || { label: status, className: 'bg-gray-500', icon: AlertTriangle };
    const Icon = config.icon;

    return (
      <Badge className={`${config.className} hover:${config.className} gap-1`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  const formatCurrency = (amount) => {
    return formatDirham(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  if (loading) return <div className="flex items-center justify-center h-96">Loading claims...</div>;

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-7 gap-4">
        <Card 
          className={`cursor-pointer transition-all ${activeFilter === 'all' ? 'ring-2 ring-lime-500' : ''}`}
          onClick={() => handleFilterClick('all')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-slate-600">TOTAL</p>
                <p className="text-2xl font-bold text-slate-800">{stats.total}</p>
              </div>
              <FileText className="w-6 h-6 text-slate-400" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`cursor-pointer transition-all ${activeFilter === 'pending' ? 'ring-2 ring-yellow-500' : ''}`}
          onClick={() => handleFilterClick('pending')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-slate-600">PENDING</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
              </div>
              <Clock className="w-6 h-6 text-yellow-400" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`cursor-pointer transition-all ${activeFilter === 'approved' ? 'ring-2 ring-green-500' : ''}`}
          onClick={() => handleFilterClick('approved')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-slate-600">APPROVED</p>
                <p className="text-2xl font-bold text-green-600">{stats.approved}</p>
              </div>
              <CheckCircle className="w-6 h-6 text-green-400" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`cursor-pointer transition-all ${activeFilter === 'rejected' ? 'ring-2 ring-red-500' : ''}`}
          onClick={() => handleFilterClick('rejected')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-slate-600">REJECTED</p>
                <p className="text-2xl font-bold text-red-600">{stats.rejected}</p>
              </div>
              <XCircle className="w-6 h-6 text-red-400" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`cursor-pointer transition-all ${activeFilter === 'paid' ? 'ring-2 ring-blue-500' : ''}`}
          onClick={() => handleFilterClick('paid')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-slate-600">PAID (LESSEE)</p>
                <p className="text-2xl font-bold text-blue-600">{stats.paidByLessee}</p>
              </div>
              <DollarSign className="w-6 h-6 text-blue-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer transition-all">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-slate-600">PAID (RIDER)</p>
                <p className="text-2xl font-bold text-purple-600">{stats.paidByRider}</p>
              </div>
              <DollarSign className="w-6 h-6 text-purple-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer transition-all">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-slate-600">PAID (COMPANY)</p>
                <p className="text-2xl font-bold text-indigo-600">{stats.paidByCompany}</p>
              </div>
              <DollarSign className="w-6 h-6 text-indigo-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Claims Table */}
      <Card>
        <CardContent className="p-6">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Claim #</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Bike</TableHead>
                  <TableHead>Lessee</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Claim Amount</TableHead>
                  <TableHead>Approved Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Police Report</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredClaims.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center text-slate-500 py-8">
                      No claims found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredClaims.map((claim) => (
                    <TableRow key={claim.id}>
                      <TableCell className="font-medium">{claim.claim_number}</TableCell>
                      <TableCell>{formatDate(claim.claim_date)}</TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">{claim.motorcycle?.plate_number || 'N/A'}</div>
                          <div className="text-sm text-slate-500">
                            {claim.motorcycle?.model_name} {claim.motorcycle?.model_version}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{claim.motorcycle?.lessee_name || 'N/A'}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">
                          {claim.claim_type}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-semibold">{formatCurrency(claim.claim_amount)}</TableCell>
                      <TableCell className="font-semibold text-green-600">
                        {claim.approved_amount ? formatCurrency(claim.approved_amount) : '-'}
                      </TableCell>
                      <TableCell>{getStatusBadge(claim.status)}</TableCell>
                      <TableCell>
                        {claim.police_report_type ? (
                          <Badge 
                            variant="outline" 
                            className={claim.police_report_type === 'red' ? 'border-red-500 text-red-600' : 'border-green-500 text-green-600'}
                          >
                            {claim.police_report_type.toUpperCase()}
                          </Badge>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewDetails(claim)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditClick(claim)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Update Insurance Claim</DialogTitle>
            <DialogDescription>
              Update the status and details for claim #{selectedClaim?.claim_number}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="status">Status *</Label>
                <Select value={editForm.status} onValueChange={(value) => setEditForm({...editForm, status: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                    {(selectedClaim?.status === 'rejected' || 
                      selectedClaim?.status === 'paid_by_lessee' || 
                      selectedClaim?.status === 'paid_by_rider' ||
                      selectedClaim?.status === 'paid_by_company') && (
                      <>
                        <SelectItem value="paid_by_lessee">Paid by Lessee</SelectItem>
                        <SelectItem value="paid_by_rider">Paid by Rider</SelectItem>
                        <SelectItem value="paid_by_company">Paid by Company</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
                {(selectedClaim?.status === 'rejected' || 
                  selectedClaim?.status === 'paid_by_lessee' || 
                  selectedClaim?.status === 'paid_by_rider' ||
                  selectedClaim?.status === 'paid_by_company') && (
                  <p className="text-xs text-slate-500 mt-1">
                    💡 Payment options available for rejected claims
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="approved_amount">Approved Amount (Dh)</Label>
                <Input
                  id="approved_amount"
                  type="number"
                  step="0.01"
                  value={editForm.approved_amount}
                  onChange={(e) => setEditForm({...editForm, approved_amount: e.target.value})}
                  placeholder="0.00"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="settlement_date">Settlement Date</Label>
                <Input
                  id="settlement_date"
                  type="date"
                  value={editForm.settlement_date}
                  onChange={(e) => setEditForm({...editForm, settlement_date: e.target.value})}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="payment_method">Payment Method</Label>
                <Select value={editForm.payment_method} onValueChange={(value) => setEditForm({...editForm, payment_method: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select method" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="insurance">Insurance</SelectItem>
                    <SelectItem value="lessee">Lessee</SelectItem>
                    <SelectItem value="rider">Rider</SelectItem>
                    <SelectItem value="company">Company</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={editForm.notes}
                onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                placeholder="Add any additional notes..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateClaim}>
              Update Claim
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Claim Details</DialogTitle>
            <DialogDescription>
              Complete information for claim #{selectedClaim?.claim_number}
            </DialogDescription>
          </DialogHeader>
          
          {selectedClaim && (
            <div className="space-y-6 py-4">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-sm text-slate-600 mb-3">Claim Information</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Claim Number:</span>
                      <span className="font-medium">{selectedClaim.claim_number}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Claim Date:</span>
                      <span className="font-medium">{formatDate(selectedClaim.claim_date)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Incident Date:</span>
                      <span className="font-medium">{formatDate(selectedClaim.incident_date)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Type:</span>
                      <span className="font-medium capitalize">{selectedClaim.claim_type}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Status:</span>
                      <div>{getStatusBadge(selectedClaim.status)}</div>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold text-sm text-slate-600 mb-3">Motorcycle Details</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Plate Number:</span>
                      <span className="font-medium">{selectedClaim.motorcycle?.plate_number || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Model:</span>
                      <span className="font-medium">
                        {selectedClaim.motorcycle?.model_name} {selectedClaim.motorcycle?.model_version}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Lessee:</span>
                      <span className="font-medium">{selectedClaim.motorcycle?.lessee_name || 'N/A'}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-sm text-slate-600 mb-3">Financial Details</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Claim Amount:</span>
                      <span className="font-semibold text-lg">{formatCurrency(selectedClaim.claim_amount)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Approved Amount:</span>
                      <span className="font-semibold text-lg text-green-600">
                        {selectedClaim.approved_amount ? formatCurrency(selectedClaim.approved_amount) : 'Not set'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Payment Method:</span>
                      <span className="font-medium capitalize">{selectedClaim.payment_method || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Settlement Date:</span>
                      <span className="font-medium">{formatDate(selectedClaim.settlement_date)}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold text-sm text-slate-600 mb-3">Additional Information</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Police Report:</span>
                      <span className="font-medium uppercase">{selectedClaim.police_report_type || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Policy Number:</span>
                      <span className="font-medium">{selectedClaim.policy_number}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Created:</span>
                      <span className="font-medium">{formatDate(selectedClaim.created_at)}</span>
                    </div>
                    {selectedClaim.updated_at && (
                      <div className="flex justify-between">
                        <span className="text-slate-600">Last Updated:</span>
                        <span className="font-medium">{formatDate(selectedClaim.updated_at)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {selectedClaim.description && (
                <div>
                  <h3 className="font-semibold text-sm text-slate-600 mb-2">Description</h3>
                  <p className="text-sm text-slate-700 bg-slate-50 p-3 rounded">{selectedClaim.description}</p>
                </div>
              )}

              {selectedClaim.notes && (
                <div>
                  <h3 className="font-semibold text-sm text-slate-600 mb-2">Notes</h3>
                  <p className="text-sm text-slate-700 bg-slate-50 p-3 rounded">{selectedClaim.notes}</p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InsuranceClaims;
